import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { RequestError, Service, ValidationErrors } from 'webapp-types';
import { ScheduleType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import ReportsShareScheduleFormComponent from 'webapp/components/reports/share/schedule/form';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Report from 'webapp/models-custom/report';
import type ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';

import type { TrackingOriginTypeForShare } from 'webapp/services/model-manager/reports';

export interface Args {
	model: Report;
	isContentShown: boolean;
	trackingOrigin: TrackingOriginTypeForShare;
	onContentChange: () => void;
	onClose: () => void;
}

export default class ReportsShareSendTabComponent<T extends Args> extends Component<T> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/report-schedules')
	declare mmReportSchedulesService: Service.ModelManager.ReportSchedules;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;

	@tracked scheduleSettingModel!: ReportScheduleSetting;

	@tracked submitInProgress = false;

	@tracked errors: ValidationErrors<'deliveryOrRecipients'> = {};

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.setBlankShareSettingModel();
	}

	setBlankShareSettingModel(): void {
		this.scheduleSettingModel = this.mmReportScheduleSettingsService.createNewModel({
			reportId: this.args.model.numericId!,
			scheduleType: ScheduleType.ONE_OFF,
		});
	}

	validateFields(): boolean {
		const model = this.scheduleSettingModel,
			errors: ValidationErrors<'deliveryOrRecipients'> = {};
		this.errors = {};

		if (!model.isEmailDelivery && !model.isSlackDelivery) {
			errors.deliveryOrRecipients = [
				{
					name: 'deliveryOrRecipients',
					message: 'At least one delivery type / recipients must be selected',
				},
			];
		}

		this.errors = errors;

		return Object.keys(errors).length === 0;
	}

	@action
	save(): Promise<void> {
		if (this.submitInProgress) {
			return Promise.reject();
		}

		const model = this.scheduleSettingModel;

		if (!model.reportId) {
			this.infolayerService.error('No report selected');
			return Promise.reject();
		}

		if (!this.validateFields()) {
			return Promise.reject();
		}

		this.submitInProgress = true;

		model.cleanupUnusedScheduleValues();

		const layer = this.infolayerService.loading('Saving...'),
			reportTitle = model.report?.title,
			isEmail = model.recipients.length > 0;

		const successText = model.isNew
			? model.isOneOff
				? `Your report "${reportTitle}" (PDF) is being prepared. It will be${isEmail ? ' emailed to you, and' : ''} available in your report archive shortly.`
				: 'Your report has been successfully scheduled.'
			: 'Report schedule has been updated';

		return new Promise((resolve, reject) => {
			model
				.save()
				.then(
					() => {
						layer.ok(successText);

						const trackingData = model.getTrackingDataForCreate(
							this.args.trackingOrigin,
						);

						this.analyticsService.event(TrackingEvent.REPORT_SEND, trackingData);

						next(this, this.setBlankShareSettingModel);

						resolve();
					},
					(errorData: unknown) => {
						layer.close();
						throw errorData;
					},
				)
				.finally(() => {
					this.errors = {};
					this.submitInProgress = false;
				})
				.catch((errorData: RequestError) => {
					this.requestService.showAllValidationErrors(errorData);
					reject();
				});
		});
	}

	@action
	sendLater(): void {
		if (this.validateFields()) {
			// don't open if form not filled yet
			this.mmReportSchedulesService.openSendLaterPopup({
				onSave: (ts: number) => {
					this.scheduleSettingModel.scheduledOn = ts;
					return this.save();
				},
			});
		}
	}

	<template>
		<div class="content2 {{if @isContentShown 'content2--shown'}} send-now tab-send-now">

			<ReportsShareScheduleFormComponent
				@model={{this.scheduleSettingModel}}
				@isPeriodic={{false}}
				@onContentChange={{@onContentChange}}
				@submitInProgress={{this.submitInProgress}}
				@errors={{this.errors}}
			/>

			<div class="controls2">
				<div class="controls2__left">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{@onClose}}
					/>
				</div>
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Send now"
						@icon="Send"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
					<SubmitButtonComponent
						@text="Send later..."
						@icon="Hourglass"
						@extraClass="save"
						@onTrigger={{this.sendLater}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
